/**
 * 选项切换相关的工具函数 (Vue2版本)
 * 处理单选、多选选项的切换逻辑
 */
import { Message } from 'element-ui'
import { FieldType } from '../fast/enum'
import { getFilterCount } from './useFast'



/**
 * 获取当前分类的选项对象
 * @param {object} category 分类对象
 * @returns {object} 根据层级返回对应的选项对象
 */
export function getOptions(category) {
	if (category.level === 3) {
		// 3级结构：返回当前激活tab对应的选项
		return category.options?.find(x => x.id === category.tabActiveId) || {}
	}
	// 其他情况：直接返回分类本身
	return category || {}
}

/**
 * 通用的选项切换逻辑
 * @param {object} option 要切换的选项
 * @param {object} category 所属分类
 * @param {Array} filterOptions 所有筛选选项
 */
export function toggleOption(option, category, filterOptions) {
	/**
	 * 检查筛选条件数量限制
	 * @returns {boolean} 是否可以继续添加筛选条件
	 */
	function checkFilterCount() {
		const count = getFilterCount(filterOptions)
		if (count >= 50) {
            Message.warning('最多只能选择50个筛选条件')
			return false
		}
		return true
	}

	if ([FieldType.SINGLE, FieldType.LABEL].includes(category.fieldType)) {
		// 单选模式处理
		const current = filterOptions.find(x => x.id === category.id)
		if (!current) {
			return
		}
		const currentValues = current.value || []
		if (currentValues.some(v => v?.id === option.id)) {
			// 取消选择：清空所有值
			current.value = []
			category.value = []
		}
		else {
			// 选择新项：检查数量限制后设置值
			if (!checkFilterCount()) {
				return
			}
			const selectedItem = {
				id: option.id,
				name: option.name,
				nickName: option.nickname
			}
			current.value = [selectedItem]
			category.value = [selectedItem]
		}
	}
	else {
		// 多选模式处理
		const categoryValues = category.value || []
		if (categoryValues.some(v => v?.id === option.id)) {
			// 移除选项：从当前分类和所有相关分类中移除
			category.value = categoryValues.filter(v => v?.id !== option.id)
			filterOptions.forEach(item => {
				if (item.fieldType === FieldType.CHECKBOX) {
					const itemValues = item.value || []
					item.value = itemValues.filter(v => v?.id !== option.id)
				}
			})
		}
		else {
			// 添加选项：检查数量限制后添加到相关分类
			if (!checkFilterCount()) {
				return
			}
			const selectedItem = {
				id: option.id,
				name: option.name,
				nickName: option.nickname
			}
			categoryValues.push(selectedItem)
			filterOptions.forEach(item => {
				if (item.fieldType === FieldType.CHECKBOX) {
					// 检查该选项是否属于当前分类的层级结构
					const level2include = item.level === 2 && item.options?.some(x => x.id === option.id && x.name === option.name)
					const level3include = item.level === 3 && item.options?.some(x => x.options?.some(y => y.id === option.id && y.name === option.name))
					if (level2include || level3include) {
						const itemValues = item.value || []
						if (!itemValues.some(v => v?.id === option.id)) {
							itemValues.push(selectedItem)
						}
					}
				}
			})
		}


		// 更新全选状态：检查当前选项卡的所有选项是否都被选中
		const currentCategoryValues = category.value
		category.selectAll[category.tabActiveId] = getOptions(category).options?.every((x) => currentCategoryValues.some(v => v?.id === x?.id)) ?? false
	}
}

/**
 * 检查选项是否属于指定分类
 * @param {object} option 选项
 * @param {object} category 分类
 * @returns {boolean} 是否属于该分类
 */
function isOptionBelongsToCategory(option, category) {
	if (!category.options) {
		return false
	}

	// 检查直接子选项
	if (category.options.some(opt => opt.id === option.id)) {
		return true
	}

	// 检查嵌套子选项
	return category.options.some(opt => {
		if (opt.options) {
			return opt.options.some(subOpt => subOpt.id === option.id)
		}
		return false
	})
}

/**
 * 切换全选状态
 * @param {object} category 分类对象
 * @param {Array} filterOptions 所有筛选选项
 * @param {Function} showToast 显示提示的函数
 */
export function toggleSelectAll(category, filterOptions, showToast) {
	const options = getOptions(category)
	if (!options.options) {
		return
	}

	const categoryValues = category.value || []
	const allSelected = options.options.every(opt =>
		categoryValues.some(v => v?.id === opt.id)
	)

	if (allSelected) {
		// 取消全选
		options.options.forEach(opt => {
			toggleOption(opt, category, filterOptions, showToast)
		})
	}
	else {
		// 全选
		options.options.forEach(opt => {
			if (!categoryValues.some(v => v?.id === opt.id)) {
				toggleOption(opt, category, filterOptions, showToast)
			}
		})
	}
}

/**
 * 检查选项是否被选中
 * @param {object} option 选项
 * @param {object} category 分类
 * @returns {boolean} 是否被选中
 */
export function isOptionSelected(option, category) {
	const categoryValues = category.value || []
	return categoryValues.some(v => v?.id === option.id)
}

/**
 * 检查是否全选
 * @param {object} category 分类对象
 * @returns {boolean} 是否全选
 */
export function isAllSelected(category) {
	const options = getOptions(category)
	if (!options.options || options.options.length === 0) {
		return false
	}

	const categoryValues = category.value || []
	return options.options.every(opt =>
		categoryValues.some(v => v?.id === opt.id)
	)
}

/**
 * 检查是否部分选中
 * @param {object} category 分类对象
 * @returns {boolean} 是否部分选中
 */
export function isIndeterminate(category) {
	const options = getOptions(category)
	if (!options.options || options.options.length === 0) {
		return false
	}

	const categoryValues = category.value || []
	const selectedCount = options.options.filter(opt =>
		categoryValues.some(v => v?.id === opt.id)
	).length

	return selectedCount > 0 && selectedCount < options.options.length
}

/**
 * 获取选中项的数量
 * @param {object} category 分类对象
 * @returns {number} 选中项数量
 */
export function getSelectedCount(category) {
	const categoryValues = category.value || []
	return categoryValues.length
}

/**
 * 清空分类的所有选择
 * @param {object} category 分类对象
 * @param {Array} filterOptions 所有筛选选项
 */
export function clearCategorySelection(category, filterOptions) {
	const categoryValues = category.value || []

	// 清空当前分类的选择
	category.value = []

	// 从其他相关分类中移除这些选项
	categoryValues.forEach(selectedItem => {
		filterOptions.forEach(item => {
			if (item.id !== category.id && item.fieldType === FieldType.CHECKBOX) {
				const itemValues = item.value || []
				item.value = itemValues.filter(v => v?.id !== selectedItem.id)
			}
		})
	})
}
